import React, { useMemo, useState, useEffect } from "react";
import dayjs from "../../../../utils/dayjsConfig";
import { NumericFormat } from "react-number-format";
import { Keyboard } from "@capacitor/keyboard";
import { Capacitor, PluginListenerHandle } from "@capacitor/core";

// CONSTANTS
import { operateSectionMobileTabs } from "../../constants";

// UTILS
import { defaultNumericFormatProps } from "../../../../utils";

// TYPES
import { OperateSectionMobileProps } from "../../types";

// COMPONENTS
import { ModalBody, ModalFooter, ModalHeader } from "@nextui-org/react";
import Button from "../../../Button/Button";
import { BackHeader } from "../BackHeader/BackHeader";
import TabsUnderline from "../../../TabsUnderline/TabsUnderline";
import AmountSelectMobile from "../AmountSelectMobile/AmountSelectMobile";
import OptionsBottomSheet from "../../../OptionsBottomSheet/OptionsBottomSheet";
import Alert from "../../../Alert/Alert";

//STYLES
import styles from "./OperateSectionMobile.module.scss";

// ICONS
import DropdownIcon from "../../../../assets/icons/dropdown-down-icon.svg?react";
import { useAppSelector } from "../../../../store/hooks";

// Market hours validator for Argentina (America/Argentina/Buenos_Aires)
const isArgentinaMarketOpen = (): boolean => {
  const now = dayjs().tz("America/Argentina/Buenos_Aires");
  const dayOfWeek = now.day(); // 0 = Sunday, 6 = Saturday
  if (dayOfWeek === 0 || dayOfWeek === 6) return false;

  const openTime = now.hour(10).minute(45).second(0).millisecond(0);
  const closeTime = now.hour(16).minute(30).second(0).millisecond(0);

  return now.isBetween(openTime, closeTime, undefined, "[]");
};

const OperateSectionMobile: React.FC<OperateSectionMobileProps> = React.memo(
  ({
    values,
    setFieldValue,
    errors,
    isValid,
    availableArs,
    availableMep,
    handleSubmit,
    handleClose,
    setValues,
    accountOptions,
    handleAccountChange,
    symbolPositionLoading,
    mepPriceToShow,
  }) => {
    const {
      priceContainer,
      priceText,
      footer,
      minimumAmount,
      errorText,
      bodyContent,
      content,
      alertError,
      button,
      modalHeader,
      accountSelectorInputButton,
      accountSelectorContent,
      accountSelectorValue,
      accountSelectorIcon,
      body,
    } = styles;

    const { isHolidayToday } = useAppSelector((state) => state.marketStatus);
    const isMarketOpen = isArgentinaMarketOpen() && !isHolidayToday;

    const [sideSelected, setSideSelected] = useState<"BUY" | "SELL">(
      values.side,
    );
    const [accountSelectorOpen, setAccountSelectorOpen] = useState(false);
    const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
    const [keyboardHeight, setKeyboardHeight] = useState<number>(0);
    const isBuySide = values.side === "BUY";
    const isNative = Capacitor.isNativePlatform();
    const isWebKeyboardOpen = !isNative && isKeyboardOpen;

    // Apply dynamic padding-bottom to ModalBody
    useEffect(() => {
      // Find ModalBody element by bodyMobileContainer class
      const modalBodyElement = document.querySelector(
        `.bodyMobileContainer`,
      ) as HTMLElement;

      if (modalBodyElement) {
        let paddingBottom = "3rem";

        if (isKeyboardOpen && keyboardHeight > 0) {
          // Adjust padding: use approximately 70% of keyboardHeight
          // or subtract 60-80px to compensate for extra space included by Capacitor
          const adjustedHeight = Math.max(
            keyboardHeight * 0.7,
            keyboardHeight - 80,
          );
          paddingBottom = `${Math.round(adjustedHeight)}px`;
        }

        // Use setProperty with important to override the !important from CSS
        modalBodyElement.style.setProperty(
          "padding-bottom",
          paddingBottom,
          "important",
        );
      }
    }, [isKeyboardOpen, keyboardHeight]);

    // Detect if keyboard is open and its height using Capacitor Keyboard
    useEffect(() => {
      if (!Capacitor.isNativePlatform()) {
        return;
      }

      let keyboardWillShowListener: PluginListenerHandle | undefined;
      let keyboardDidShowListener: PluginListenerHandle | undefined;
      let keyboardWillHideListener: PluginListenerHandle | undefined;
      let keyboardDidHideListener: PluginListenerHandle | undefined;

      const setupListeners = async () => {
        keyboardWillShowListener = await Keyboard.addListener(
          "keyboardWillShow",
          (info) => {
            setIsKeyboardOpen(true);
            setKeyboardHeight(info.keyboardHeight || 0);
          },
        );

        keyboardDidShowListener = await Keyboard.addListener(
          "keyboardDidShow",
          (info) => {
            setIsKeyboardOpen(true);
            setKeyboardHeight(info.keyboardHeight || 0);
          },
        );

        keyboardWillHideListener = await Keyboard.addListener(
          "keyboardWillHide",
          () => {
            setIsKeyboardOpen(false);
            setKeyboardHeight(0);
          },
        );

        keyboardDidHideListener = await Keyboard.addListener(
          "keyboardDidHide",
          () => {
            setIsKeyboardOpen(false);
            setKeyboardHeight(0);
          },
        );
      };

      setupListeners();

      return () => {
        keyboardWillShowListener?.remove();
        keyboardDidShowListener?.remove();
        keyboardWillHideListener?.remove();
        keyboardDidHideListener?.remove();
      };
    }, []);

    useEffect(() => {
      if (Capacitor.isNativePlatform()) return;
      const vv = window.visualViewport;
      if (!vv) return;
      const initialHeight = vv.height;
      const onResize = () => {
        const open = vv.height < initialHeight - 50;
        setIsKeyboardOpen(open);
        setKeyboardHeight(0); // web: visualViewport already shrinks, no padding needed
      };
      vv.addEventListener("resize", onResize);
      return () => vv.removeEventListener("resize", onResize);
    }, []);

    // -------- Aux functions -------- //

    const getAccountLabel = () => {
      const selectedAccount = accountOptions.find(
        (acc) => acc.value === values.account,
      );
      return selectedAccount ? selectedAccount.label : "Cuenta Comitente";
    };
    const [accountSelected, setAaccountSelected] = useState(getAccountLabel());

    const resetFields = () => {
      setFieldValue("operationValueArs", "");
      setFieldValue("operationValueMep", "");
      setFieldValue("isTotal", false);
    };

    const getInfoText = () => {
      if (!!values?.operationValueArs && !!errors.operationValueArs)
        return errors.operationValueArs;
      if (!!values?.operationValueMep && !!errors.operationValueMep)
        return errors.operationValueMep;
      return isBuySide
        ? "Podés comprar hasta 50.000 dólares MEP. "
        : "El monto mínimo es 1 dólar MEP.";
    };

    const showErrorText =
      !isValid && !!values?.operationValueArs && !!values?.operationValueMep;

    // -------- Aux components -------- //

    const AccountSelector = useMemo(() => {
      const isDisabled = accountOptions.length <= 1;
      return (
        <>
          <button
            onClick={() => setAccountSelectorOpen(true)}
            className={`${accountSelectorInputButton} ${isDisabled ? "bg-[#D9D9D9] border-[#D9D9D9]" : ""}`}
            disabled={isDisabled}
          >
            <div className={accountSelectorContent}>
              <div>
                <div className={accountSelectorValue}>{accountSelected}</div>
              </div>
              {accountOptions.length > 1 && (
                <DropdownIcon className={accountSelectorIcon} />
              )}
            </div>
          </button>

          <OptionsBottomSheet
            open={accountSelectorOpen}
            onClose={() => setAccountSelectorOpen(false)}
            title={"Cuenta Comitente"}
            options={
              accountOptions?.map((accountOpt) => {
                return {
                  label: accountOpt.label,
                  key: accountOpt.value,
                  onClick: () => {
                    handleAccountChange(accountOpt.value);
                    setAaccountSelected(accountOpt.label);
                  },
                };
              }) ?? []
            }
            value={values?.account}
          />
        </>
      );
    }, [
      accountOptions,
      values.account,
      handleAccountChange,
      accountSelectorOpen,
      accountSelected,
    ]);

    const SideSelector = useMemo(
      () => (
        <TabsUnderline
          aria-label=""
          items={operateSectionMobileTabs}
          optionTab={sideSelected}
          setOptionTab={(value: string) => {
            setSideSelected(value === "BUY" ? "BUY" : "SELL");
            setFieldValue("side", value);
            resetFields();
          }}
          defaultSelectedKey={values.side}
          classNames={{
            base: "w-full mb-3 mt-[1.5rem]",
            tabContent: ["text-[0.7025rem]", "font-semibold"].join(" "),
          }}
          size="lg"
        />
      ),
      [values.side, sideSelected, resetFields],
    );

    const Price = useMemo(
      () => (
        <div className={priceContainer}>
          <span className={priceText}>
            Estás {isBuySide ? "comprando" : "vendiendo"} a $
            <NumericFormat
              {...defaultNumericFormatProps}
              prefix=""
              value={isBuySide ? mepPriceToShow : mepPriceToShow}
            />
          </span>
        </div>
      ),
      [values.side, mepPriceToShow, mepPriceToShow, isBuySide],
    );

    const AmountSelector = useMemo(
      () => (
        <AmountSelectMobile
          setFieldValue={setFieldValue}
          setValues={setValues}
          isBuyingSide={isBuySide}
          operationValueArs={values.operationValueArs}
          operationValueMep={values.operationValueMep}
          isTotal={values.isTotal}
          availableArs={availableArs?.toString()}
          availableMep={availableMep?.toString()}
          resetFields={resetFields}
          currencySelected={values.currencySelected}
          symbolPositionLoading={symbolPositionLoading}
        />
      ),
      [
        values.operationValueArs,
        values.operationValueMep,
        values.currencySelected,
        values.isTotal,
        availableArs,
        availableMep,
        isBuySide,
        symbolPositionLoading,
      ],
    );

    const MinimumAmountText = useMemo(
      () => (
        <Alert
          variant={showErrorText ? "ERROR" : "INFO"}
          className={`${minimumAmount} ${showErrorText ? errorText : ""}`}
        >
          {getInfoText()}
        </Alert>
      ),
      [
        showErrorText,
        values.operationValueArs,
        values.operationValueMep,
        isBuySide,
        errors,
      ],
    );

    return (
      <>
        <ModalHeader className={modalHeader}>
          <BackHeader onClose={handleClose} />
        </ModalHeader>

        <ModalBody className={`${body} bodyMobileContainer`}>
          <div className={content} style={isWebKeyboardOpen ? { paddingBottom: 0 } : undefined}>
            {!isMarketOpen && (
              <Alert variant="ERROR" className={alertError}>
                El mercado está cerrado. Podés operar de 10:45 a 16:30.
              </Alert>
            )}
            <div className={bodyContent}>
              {AccountSelector}
              {SideSelector}
              {Price}
              {MinimumAmountText}
              {AmountSelector}
            </div>
          </div>
        </ModalBody>
        {!isWebKeyboardOpen && (
          <ModalFooter className={` ${footer} flex-col`}>
            <div>
              <Button
                className={button}
                text="Cancelar"
                variant="secondary"
                onPress={handleClose}
              />
              <Button
                className={button}
                text="Continuar"
                onPress={() => handleSubmit()}
                isDisabled={
                  !isValid ||
                  !values.operationValueArs ||
                  !values.operationValueMep ||
                  !isMarketOpen
                }
              />
            </div>
          </ModalFooter>
        )}
      </>
    );
  },
);

export default OperateSectionMobile;
